import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

const dotFallingLoaderVariants = cva('flex items-center justify-center', {
  variants: {
    size: {
      xs: 'gap-3xs',
      sm: 'gap-2xs',
      md: 'gap-xs',
      lg: 'gap-sm',
    },
    color: {
      current: 'text-current',
      base: 'text-base-main',
      neutral: 'text-neutral-main',
      positive: 'text-positive-main',
      negative: 'text-negative-main',
      alert: 'text-alert-main',
      white: 'text-white',
      black: 'text-black',
    },
  },
  defaultVariants: {
    size: 'md',
    color: 'current',
  },
})

const dotVariants = cva('rounded-full bg-current', {
  variants: {
    size: {
      xs: 'w-1 h-1',
      sm: 'w-1.5 h-1.5',
      md: 'w-2 h-2',
      lg: 'w-2.5 h-2.5',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

export type DotFallingLoaderVariants = VariantProps<
  typeof dotFallingLoaderVariants
>

export interface DotFallingLoaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'>,
    DotFallingLoaderVariants {
  /**
   * Number of dots to display (default: 3)
   */
  dotCount?: number
  /**
   * Animation duration in seconds (default: 1.4)
   */
  duration?: number
}

const DotFallingLoader = React.forwardRef<
  HTMLDivElement,
  DotFallingLoaderProps
>(
  (
    {
      className,
      size = 'md',
      color = 'current',
      dotCount = 3,
      duration = 1.4,
      ...props
    },
    ref,
  ) => {
    const dots = Array.from({ length: dotCount }, (_, index) => (
      <div
        key={index}
        className={cn(
          dotVariants({ size }),
          `animate-dot-falling-${index + 1}`,
        )}
        style={{
          animationDuration: `${duration}s`,
        }}
      />
    ))

    return (
      <div
        ref={ref}
        className={cn(dotFallingLoaderVariants({ size, color }), className)}
        {...props}
      >
        {dots}
      </div>
    )
  },
)

DotFallingLoader.displayName = 'DotFallingLoader'

export { DotFallingLoader, dotFallingLoaderVariants, dotVariants }
